// Channel ideas (/idea). Four pieces:
//   IdeaModal — the /idea dialog (headline + optional detail) → IdeasApi.create
//   IdeaCard  — the in-stream card (rendered by MessageList for metadata kind "idea")
//   IdeaPanel — the discussion, opened from the card: back it, read every contribution, add yours
//   PromoteForm — staff-only footer of the panel: turn the idea into a TMCHub story
// The whole idea lives in the message's metadata; backing/contributing broadcasts MessageEdited,
// so the single card in the chat is always current no matter how long the discussion runs.

import { useEffect, useRef, useState } from "react";
import { IdeasApi, ChannelsApi, UsersApi } from "../hooks/useApi.js";
import Icon from "./Icon.jsx";
import Avatar from "./Avatar.jsx";

export function parseIdeaMeta(m) {
  try {
    const meta = JSON.parse(m.metadataJson || "");
    return meta?.kind === "idea" ? meta : null;
  } catch { return null; }
}

// "2:14 PM" today, "Yesterday", else "24 Jun" — contributions are read in sequence, so the
// timestamp only needs to separate them, not date-stamp each one precisely.
function whenLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso); if (isNaN(d)) return "";
  const today = new Date();
  const yest = new Date(today); yest.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (d.toDateString() === yest.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

const backerList = (meta) => Object.values(meta.backers || {});

// A channel that maps to a real TMCHub product. The Dream Team ops channel does not — it carries
// placeholder ids — so ideas raised there name the product they're about instead.
export const isProductChannel = (c) => !!c && !c.kind && c.tmcHubProductId > 0;
const productChannels = (channels) => channels.filter(isProductChannel);

// An idea can become several stories. `storyIds` carries them all; `id` is the first one and is what
// older promoted cards have, so fall back to it.
const promotedIds = (p) => (p?.storyIds?.length ? p.storyIds : p?.id ? [p.id] : []);
const promotedShort = (p) => {
  const ids = promotedIds(p);
  return ids.length > 1 ? `now ${ids.length} stories` : ids.length ? `now story #${ids[0]}` : "now a story";
};
const promotedLabel = (p) => {
  const ids = promotedIds(p);
  if (ids.length > 1) return `Became ${ids.length} stories — #${ids.join(", #")}`;
  return ids.length ? `Turned into story #${ids[0]}` : "Turned into a story";
};

// ── the in-stream card ──────────────────────────────────────────────────────

export function IdeaCard({ m, currentUserId, onOpen }) {
  const meta = parseIdeaMeta(m);
  if (!meta) return null;
  const contributions = meta.contributions || [];
  const backers = backerList(meta);
  const latest = contributions[contributions.length - 1];
  // Distinct people in the discussion, starter first — the avatar stack answers "who's on this".
  const faces = [];
  for (const c of [{ userId: meta.createdBy, name: meta.createdByName }, ...contributions])
    if (!faces.some((f) => f.userId === c.userId)) faces.push({ userId: c.userId, name: c.name });

  return (
    <div className={`idea-card ${meta.closed ? "idea-card--closed" : ""}`}
         role="button" tabIndex={0} onClick={() => onOpen?.(m)}
         onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(m); } }}>
      <span className="card-scan" aria-hidden />
      <div className="idea-card__head">
        <span className="idea-card__icon"><Icon name="idea" /></span>
        <div className="idea-card__ht">
          <strong className="idea-card__title">{meta.title}</strong>
          <span className="idea-card__by">
            started by {meta.createdByName}
            {meta.productName ? <span className="idea-card__product"> · {meta.productName}</span> : null}
            {meta.promoted ? ` · ${promotedShort(meta.promoted)}` : meta.closed ? " · closed" : ""}
          </span>
        </div>
        <span className="card-tag card-tag--idea">Idea</span>
      </div>

      <div className="idea-card__stats">
        <span className="idea-card__faces">
          {faces.slice(0, 4).map((f) => (
            <Avatar key={f.userId} userId={f.userId} name={f.name} size="xs" className="idea-card__face" />
          ))}
          {faces.length > 4 && <span className="idea-card__more">+{faces.length - 4}</span>}
        </span>
        <span className="idea-card__count">
          👍 {backers.length} · {contributions.length} contribution{contributions.length === 1 ? "" : "s"}
        </span>
      </div>

      {latest && <div className="idea-card__latest">“{latest.text}”</div>}

      <div className="idea-card__foot">
        <span className="idea-card__cta">
          {meta.closed ? "Open" : (meta.backers || {})[String(currentUserId)] ? "Open discussion" : "Back it & join in"}
          <Icon name="chevron-right" className="ic--sm" />
        </span>
      </div>
    </div>
  );
}

// ── the discussion panel ────────────────────────────────────────────────────

export function IdeaPanel({ m, currentUserId, isStaff, channels = [], currentChannel = null, onClose }) {
  const meta = parseIdeaMeta(m);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [promoting, setPromoting] = useState(false);
  const listRef = useRef(null);
  const contributions = meta?.contributions || [];

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Follow the discussion as it grows (the panel stays open while others contribute live).
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [contributions.length]);

  if (!meta) return null;
  const backers = backerList(meta);
  const iBack = !!(meta.backers || {})[String(currentUserId)];
  const canManage = currentUserId === meta.createdBy || isStaff;

  const act = async (fn) => {
    if (busy) return;
    setBusy(true); setError("");
    try { await fn(); }
    catch (e) { setError(e?.body?.error || "That didn't go through. Try again."); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    const t = text.trim();
    if (!t || busy) return;
    // Clear optimistically so the box is ready for the next thought; restore it if the post failed.
    setText("");
    await act(() => IdeasApi.contribute(m.id, t).catch((e) => { setText(t); throw e; }));
  };

  return (
    <div className="modal-overlay is-open" onClick={onClose}>
      <div className="modal idea-panel" onClick={(e) => e.stopPropagation()}>
        <header className="idea-panel__head">
          <span className="idea-panel__icon"><Icon name="idea" /></span>
          <div className="idea-panel__ht">
            <span className="idea-panel__kicker">
              Idea{meta.promoted ? ` · ${promotedShort(meta.promoted)}` : meta.closed ? " · closed" : ""}
            </span>
            <h3 className="idea-panel__title">{meta.title}</h3>
            <p className="idea-panel__by">
              started by {meta.createdByName}
              {meta.productName ? <> · <b>{meta.productName}</b></> : null}
            </p>
          </div>
          <button type="button" className="modal__close" onClick={onClose}><Icon name="close" /></button>
        </header>

        <div className="idea-panel__body">
          {meta.description ? <p className="idea-panel__desc">{meta.description}</p> : null}

          <div className="idea-panel__backing">
            <button type="button"
                    className={`idea-back ${iBack ? "is-mine" : ""}`}
                    disabled={busy || meta.closed}
                    onClick={() => act(() => IdeasApi.back(m.id))}>
              👍 <span className="idea-back__n">{backers.length}</span>
              <span className="idea-back__label">{iBack ? "You back this" : "Back this"}</span>
            </button>
            {backers.length > 0 && (
              <span className="idea-panel__backers" title={backers.join(", ")}>{backers.join(", ")}</span>
            )}
          </div>

          {error && <div className="field__err">⚠ {error}</div>}

          <div className="idea-panel__label">
            {contributions.length} contribution{contributions.length === 1 ? "" : "s"}
          </div>
          <div className="idea-panel__list" ref={listRef}>
            {contributions.length === 0 && (
              <p className="idea-panel__empty">No one's added to this yet — go first.</p>
            )}
            {contributions.map((c) => (
              <div className="idea-contrib" key={c.id}>
                <Avatar userId={c.userId} name={c.name} size="sm" className="idea-contrib__av" />
                <div className="idea-contrib__body">
                  <div className="idea-contrib__meta">
                    <b className="idea-contrib__name">{c.name}</b>
                    <span className="idea-contrib__when">{whenLabel(c.at)}</span>
                    {(c.userId === currentUserId || isStaff) && (
                      <button type="button" className="idea-contrib__rm" title="Remove this contribution"
                              disabled={busy}
                              onClick={() => act(() => IdeasApi.removeContribution(m.id, c.id))}>
                        <Icon name="close" className="ic--sm" />
                      </button>
                    )}
                  </div>
                  <p className="idea-contrib__text">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {!meta.closed && (
          <div className="idea-panel__composer">
            <textarea
              className="input textarea idea-panel__input"
              rows={2} maxLength={2000} value={text} autoFocus
              placeholder="Build on this idea…"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
            />
            <button type="button" className="btn btn--primary" disabled={busy || !text.trim()} onClick={submit}>
              {busy ? "Adding…" : "Add"}
            </button>
          </div>
        )}

        <footer className="idea-panel__foot">
          {meta.promoted ? (
            <span className="idea-panel__promoted">
              ✅ {promotedLabel(meta.promoted)} by {meta.promoted.by}
            </span>
          ) : (
            <>
              {canManage && (
                <button type="button" className="btn btn--soft" disabled={busy}
                        onClick={() => act(() => (meta.closed ? IdeasApi.reopen(m.id) : IdeasApi.close(m.id)))}>
                  {meta.closed ? "Reopen idea" : "Close idea"}
                </button>
              )}
              {isStaff && (
                <button type="button" className="btn btn--primary" onClick={() => setPromoting(true)}>
                  Turn into stories
                </button>
              )}
            </>
          )}
        </footer>

        {promoting && !meta.promoted && (
          <IdeaPromoteModal m={m} meta={meta} channels={channels} currentChannel={currentChannel}
                            onCancel={() => setPromoting(false)} onDone={onClose} />
        )}
      </div>
    </div>
  );
}

// Staff-only: idea → TMCHub stories, in two steps. First Claude reads the idea and its whole
// discussion and proposes the stories it implies (title, write-up, estimate, suggested owner,
// subtasks) — creating nothing. Staff then review and edit those rows and create them for real.
// Same shape as the meeting-notes "Create stories" dialog, and reuses its markup/CSS.
//
// The product matters: an idea raised in the Dream Team ops channel isn't tied to one, so staff pick
// the product the stories belong to (the server rejects a story filed against that channel's
// placeholder ids). Platform decides which build's Client Testing card picks the story up.
export function IdeaPromoteModal({ m, meta, channels = [], currentChannel = null, onCancel, onDone }) {
  // Where the stories go: the product named when the idea was raised, else this channel when it is
  // one. Only ask when neither gives us a product.
  const ownProduct = isProductChannel(currentChannel) ? currentChannel.id : null;
  const chosenProduct = meta.productChannelId ?? ownProduct;
  const needsTarget = !chosenProduct;
  const products = productChannels(channels);

  const [phase, setPhase] = useState("planning"); // planning | review | creating
  const [summary, setSummary] = useState("");
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [targetId, setTargetId] = useState(chosenProduct ? String(chosenProduct) : "");
  const [platforms, setPlatforms] = useState([]);
  const [platformId, setPlatformId] = useState("");
  const [error, setError] = useState("");
  const [aiNote, setAiNote] = useState(""); // set when we fell back to the idea as written

  // One row per proposed story; `include` lets staff drop the ones that aren't worth tracking.
  const toRows = (stories) => stories.map((s, i) => ({
    key: i, include: true,
    title: s.title || "", description: s.description || "",
    hours: s.hours || 1, isBug: !!s.isBug,
    assignedTo: s.suggestedAssigneeId || 0, // 0 = "me" (the server maps it to the caller)
    subtasks: (s.subtasks || []).map((t) => ({ ...t })),
  }));

  useEffect(() => { UsersApi.staff().then(setStaff).catch(() => setStaff([])); }, []);

  useEffect(() => {
    let live = true;
    IdeasApi.plan(m.id)
      .then((res) => {
        if (!live) return;
        setSummary(res?.summary || "");
        setRows(toRows(res?.stories || []));
        setPhase("review");
      })
      .catch((e) => {
        if (!live) return;
        // No API key, or the planner failed — fall back to one story from the idea as written, so
        // promoting still works rather than dead-ending on an error.
        setAiNote(e?.body?.error || "Couldn't plan stories automatically.");
        setRows(toRows([{ title: meta.title, description: "", hours: 1 }]));
        setPhase("review");
      });
    return () => { live = false; };
  }, [m.id]);

  // Platforms belong to the TARGET product, which may not be the channel we're standing in.
  useEffect(() => {
    let live = true;
    setPlatforms([]); setPlatformId("");
    if (!targetId) return undefined;
    ChannelsApi.platforms(targetId)
      .then((res) => {
        if (!live) return;
        const list = res?.platforms || [];
        setPlatforms(list);
        if (res?.defaultPlatformId && list.some((p) => p.id === res.defaultPlatformId)) setPlatformId(String(res.defaultPlatformId));
        else if (list.length === 1) setPlatformId(String(list[0].id));
      })
      .catch(() => { /* the server falls back to the product's default */ });
    return () => { live = false; };
  }, [targetId]);

  const patch = (key, p) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const selected = rows.filter((r) => r.include && r.title.trim());
  const busy = phase === "creating";

  const create = async () => {
    if (!selected.length || busy || !targetId) return;
    setPhase("creating"); setError("");
    try {
      await IdeasApi.promote(m.id, {
        targetChannelId: Number(targetId),
        platformId: platformId ? Number(platformId) : null,
        stories: selected.map((r) => ({
          title: r.title.trim(),
          description: r.description,
          hours: Number(r.hours) || 1,
          isBug: r.isBug,
          assignedToUserId: r.assignedTo || 0,
          subtasks: r.subtasks.map((s) => ({ title: s.title, hours: Number(s.hours) || 1 })),
        })),
      });
      onDone?.();
    } catch (e) {
      setError(e?.body?.error || "Couldn't create the stories.");
      setPhase("review");
    }
  };

  return (
    <div className="modal-overlay is-open" onClick={() => !busy && onCancel?.()}>
      <div className="modal storymodal" onClick={(e) => e.stopPropagation()}>
        <div className="storymodal__head">
          <h3>📋 Turn the idea into stories</h3>
          <span className="storymodal__subtitle">💡 {meta.title} — review what to track</span>
        </div>

        {phase === "planning" ? (
          <div className="storymodal__list">
            <p className="storymodal__empty">Reading the discussion and drafting stories…</p>
          </div>
        ) : (
          <>
            {aiNote && <div className="idea-promote__hint">⚠ {aiNote} Edit the story below and create it by hand.</div>}
            {summary && <div className="idea-promote__hint">{summary}</div>}

            <div className="storymodal__list">
              {rows.map((r) => (
                <div key={r.key} className="storymodal__row">
                  <input type="checkbox" className="storymodal__check" disabled={busy}
                    checked={r.include} onChange={(e) => patch(r.key, { include: e.target.checked })} />
                  <div className="storymodal__main">
                    <input className="input storymodal__title" value={r.title} disabled={busy}
                      onChange={(e) => patch(r.key, { title: e.target.value })} />
                    <textarea className="input textarea" rows={3} value={r.description} disabled={busy}
                      placeholder="What needs building, and the constraints the channel settled on…"
                      onChange={(e) => patch(r.key, { description: e.target.value })} />
                    {r.subtasks.length > 0 && (
                      <div className="storymodal__subs">
                        {r.subtasks.map((s, si) => (
                          <span key={si} className="storymodal__chip">
                            {s.title} · {s.hours || 1}h
                            <button type="button" title="Drop this step" disabled={busy}
                              onClick={() => patch(r.key, { subtasks: r.subtasks.filter((_, x) => x !== si) })}>✕</button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="storymodal__opts">
                      <label className="storymodal__hours">~
                        <input type="number" min="0.5" step="0.5" className="input" value={r.hours} disabled={busy}
                          onChange={(e) => patch(r.key, { hours: e.target.value })} />h
                      </label>
                      <select className="input storymodal__assignee" value={r.assignedTo || 0} disabled={busy}
                        onChange={(e) => patch(r.key, { assignedTo: Number(e.target.value) })}>
                        <option value={0}>Assign to me</option>
                        {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                      <label className="storymodal__bug">
                        <input type="checkbox" checked={r.isBug} disabled={busy}
                          onChange={(e) => patch(r.key, { isBug: e.target.checked })} /> bug
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="idea-promote">
              {/* Shown whenever the idea lives outside a product channel — pre-selected from the
                  product picked when it was raised, so this is a confirm rather than a chore. */}
              {!ownProduct && (
                <>
                  <div className="idea-promote__label">Which product?</div>
                  <p className="idea-promote__hint">
                    {needsTarget
                      ? "This idea isn't tied to a product, so pick where the stories belong."
                      : "From the idea — change it if the stories belong somewhere else."}
                  </p>
                  <select className="input select" value={targetId} disabled={busy}
                          onChange={(e) => setTargetId(e.target.value)}>
                    <option value="">Select a product…</option>
                    {products.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </>
              )}
              {platforms.length > 1 && (
                <select className="input select" value={platformId} disabled={busy}
                        onChange={(e) => setPlatformId(e.target.value)}>
                  <option value="">Platform — the product's default</option>
                  {platforms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}
            </div>
          </>
        )}

        {error && <div className="field__err storymodal__err">⚠ {error}</div>}
        <div className="modal__foot">
          <button type="button" className="btn btn--soft" disabled={busy} onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn--primary"
                  disabled={busy || phase === "planning" || selected.length === 0 || !targetId}
                  onClick={create}>
            {busy ? <span className="btn__spin" /> : `Create ${selected.length || ""} ${selected.length === 1 ? "story" : "stories"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── the /idea dialog ────────────────────────────────────────────────────────

export function IdeaModal({ channel, channels = [], initialTitle = "", onClose }) {
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState("");
  const [productId, setProductId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Outside a product channel (the Dream Team ops channel), say up front which product the idea is
  // about — promotion then files its stories there without asking again. Optional: Dream Team carries
  // genuinely cross-product and internal ideas too.
  const needsProduct = !isProductChannel(channel);
  const products = productChannels(channels);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const create = async () => {
    if (!title.trim() || busy) return;
    setBusy(true); setError("");
    try {
      await IdeasApi.create(channel.id, title.trim(), description.trim(), productId ? Number(productId) : null);
      onClose();
    } catch (e) {
      setError(e?.body?.error || "Could not start the idea.");
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay is-open" onClick={onClose}>
      <div className="modal idea-modal" onClick={(e) => e.stopPropagation()}>
        <header className="idea-modal__head">
          <span className="idea-modal__icon"><Icon name="idea" /></span>
          <div>
            <h3>New idea</h3>
            <p>Everyone in #{channel?.name} can back it and add to the discussion.</p>
          </div>
        </header>
        {error && <div className="field__err">⚠ {error}</div>}
        <input
          className="input idea-modal__title" value={title} autoFocus maxLength={200}
          placeholder="What's the idea?" onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); create(); } }}
        />
        <textarea
          className="input textarea" rows={4} maxLength={4000} value={description}
          placeholder="Any detail worth starting from? (optional)"
          onChange={(e) => setDescription(e.target.value)}
        />
        {needsProduct && (
          <label className="idea-modal__field">
            <span className="idea-modal__label">Which product is this about?</span>
            <select className="input select" value={productId} onChange={(e) => setProductId(e.target.value)}>
              <option value="">Not product-specific — internal idea</option>
              {products.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        )}
        <footer className="idea-modal__foot">
          <button type="button" className="btn btn--soft" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn--primary" onClick={create} disabled={busy || !title.trim()}>
            {busy ? "Starting…" : "Start idea"}
          </button>
        </footer>
      </div>
    </div>
  );
}
